import { Injectable } from '@angular/core';
import { TimelineDescriptionService } from 'src/app/components/timeline/timeline-description.service';
import { ExceptionLoggingService } from 'src/app/components/logging/exception-logging.service';

import { pick } from 'lodash';

import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { type ApiSearchLegacy } from 'src/app/api/services/api-utils.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { type ApiListResponse, type ApiResponse } from '../api';
import { ApiService } from './api.service';
import { FoldersApiService } from './folders-api.service';
import { type Schedule } from './schedule';

@Injectable({
  providedIn: 'root',
})
export class ScheduleApiService extends ApiService<Schedule> {
  static readonly SCHEDULE_WRITABLE_FIELDS = [
    'name',
    'content',
    'distribution',
    'distributeToAll',
    'timeDefined',
    'startDate',
    'endDate',
    'startTime',
    'endTime',
    'recurrenceType',
    'recurrenceFrequency',
    'recurrenceAbsolute',
    'recurrenceDayOfWeek',
    'recurrenceDayOfMonth',
    'recurrenceWeekOfMonth',
    'recurrenceMonthOfYear',
    'recurrenceDaysOfWeek',
    'scheduleType',
    'playOnce',
    'subcompanyDistribution',
    'distributeToSubcompanies',
    'assignedUsers',
    'forceDistribution',
    'folderId',
  ];
  static readonly SCHEDULE_AP_WRITABLE_FIELDS = ['name', 'content'];
  static readonly SCHEDULE_SEARCH_FIELDS = ['name', 'id'];

  constructor(
    protected httpClient: HttpClient,
    private timelineDescriptionService: TimelineDescriptionService,
    private userStateService: UserStateService,
    protected companyStateService: CompanyStateService,
    private exceptionLoggingService: ExceptionLoggingService,
  ) {
    super(httpClient);
  }

  override getResourceUrl(): string {
    return '/v2/schedules';
  }

  override toServerModel(entity: Schedule, _isNew: boolean): any {
    return pick(
      entity,
      this.userStateService.isAssignedPublisher()
        ? ScheduleApiService.SCHEDULE_AP_WRITABLE_FIELDS
        : ScheduleApiService.SCHEDULE_WRITABLE_FIELDS,
    );
  }

  private _writeScheduleTypeName(schedule) {
    switch (schedule.scheduleType) {
      case 'override_replace':
        schedule.scheduleTypeName = 'Replace';
        break;
      case 'override_insert':
        schedule.scheduleTypeName = 'Include';
        break;
      default:
        schedule.scheduleTypeName = '';
    }
  }

  override fromServerModel(json: any): any {
    this._writeScheduleTypeName(json);
    return json;
  }

  private createSearchQuery(fields: string[], search: string): string {
    let query = '';

    for (let i in fields) {
      query += 'OR ' + fields[i] + ':~\"' + search + '\" ';
    }

    query = query.substring(3);

    return query.trim();
  }

  list(
    search: ApiSearchLegacy,
    cursor?: string,
  ): Promise<ApiListResponse<Schedule>> {
    let query = search.query
      ? this.createSearchQuery(
          ScheduleApiService.SCHEDULE_SEARCH_FIELDS,
          search.query,
        )
      : '';
    query += search.filter ? (search.query ? ' AND ' : '') + search.filter : '';
    if (Object.hasOwn(search, 'folderId')) {
      query += !search.query
        ? (query ? ' AND folderId:~"' : 'folderId:~"') +
          (search.folderId || FoldersApiService.ROOT_FOLDER_ID) +
          '"'
        : search.folderId &&
            search.folderId !== FoldersApiService.ROOT_FOLDER_ID
          ? ' AND folderHierarchy:~"' + search.folderId + '"'
          : '';
    }

    let params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('search', query)
      .set('count', search.count ? search.count.toString() : '')
      .set(
        'sort',
        search.sortBy
          ? search.sortBy + (search.reverse ? ' desc' : ' asc')
          : '',
      );

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return firstValueFrom(
      super.listAsObservable(params).pipe(
        map((resp) => {
          try {
            if (resp.items) {
              for (const item of resp.items) {
                item.useLocaldate = true;
                item.timeline =
                  this.timelineDescriptionService.updateLabel(item);
                item.expired = item.timeline.includes('Expired');
              }
            }
          } catch (e) {
            this.exceptionLoggingService.handle(
              e,
              'Failed process schedules.',
              true,
            );
          }
          return resp;
        }),
      ),
    );
  }

  get(scheduleId: string): Promise<ApiResponse<Schedule>> {
    return firstValueFrom(super.getAsObservable(scheduleId));
  }

  delete(scheduleId: string): Promise<ApiResponse<Schedule>> {
    return firstValueFrom(super.deleteAsObservable(scheduleId));
  }

  add(
    schedule: any,
    forceDistribution?: boolean,
  ): Promise<ApiResponse<Schedule>> {
    schedule.forceDistribution = forceDistribution;

    return firstValueFrom(
      super.addAsObservable(
        this.companyStateService.getSelectedCompanyId(),
        schedule,
      ),
    );
  }

  update(
    scheduleId: string,
    schedule: any,
    forceDistribution?: boolean,
  ): Promise<ApiResponse<Schedule>> {
    if (!this.userStateService.isAssignedPublisher()) {
      schedule.forceDistribution = forceDistribution;
    }

    return firstValueFrom(super.updateAsObservable(scheduleId, schedule));
  }

  updateAccessControlList(
    scheduleId: string,
    accessControlList: NonNullable<Schedule['accessControlList']>,
    comment?: string,
  ): Promise<ApiResponse<Schedule>> {
    const data =
      comment === undefined
        ? { accessControlList }
        : { accessControlList, comment };

    return firstValueFrom(
      this.httpClient.patch<ApiResponse<Schedule>>(
        `${this.APIUrl}/${scheduleId}`,
        data,
      ),
    );
  }

  getAccessControlList(
    scheduleId: string,
  ): Promise<NonNullable<Schedule['accessControlList']>> {
    return firstValueFrom(
      this.httpClient.get<ApiResponse<Schedule>>(
        `${this.APIUrl}/${scheduleId}`,
      ),
    ).then((response) => response.item?.accessControlList ?? {});
  }

  copy(scheduleId?: string, companyId?: string): Promise<any> {
    const params = new HttpParams().set('companyId', companyId);

    return firstValueFrom(
      super.requestAsObservable(scheduleId, 'PATCH', 'copy', params, null),
    );
  }

  export() {
    const companyId = this.companyStateService.getSelectedCompanyId();
    const params = new HttpParams().set('companyId', companyId);

    return firstValueFrom(
      super.requestAsObservable(null, 'GET', 'export', params),
    );
  }

  addPresentation(scheduleIds?: string[], playlistItem?: any): Promise<any> {
    const body = {
      scheduleIds: scheduleIds,
      playlistItem: playlistItem,
    };

    return firstValueFrom(
      super.requestAsObservable(null, 'PATCH', 'add-presentation', null, body),
    );
  }

  removePresentation(
    scheduleIds?: string[],
    presentationId?: string,
  ): Promise<any> {
    const body = {
      scheduleIds: scheduleIds,
      presentationId: presentationId,
    };

    return firstValueFrom(
      super.requestAsObservable(
        null,
        'PATCH',
        'remove-presentation',
        null,
        body,
      ),
    );
  }
}
